import {
	Box,
	createStyles,
	Skeleton,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { fetchSuggestPrompts, useApiContext } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Text } from '@repo/foundations';
import { space } from '@repo/theme/primitives';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import type { IUser, IWorkspace } from '../../../../api';
import { OnboardingSearchAi } from './OnboardingSearchAi';

interface OnboardingAskAiPageProps {
	user: IUser;
	workspace: IWorkspace;
	forceLoading?: boolean;
}

const useStyles = createStyles((theme) => ({
	content: {
		width: '100%',
		textAlign: 'center',
		justifyContent: 'center',
		alignItems: 'center',
		height: '475px', // height of the onboarding pages
	},
	title: {
		color: theme.other.getColor('text/primary/default'),
	},
	subtitle: {
		color: theme.other.getColor('text/secondary/default'),
	},
	searchContainer: {
		marginBottom: theme.spacing['2xl'],
	},
	recommendationsSection: {
		textAlign: 'left',
		width: '100%',
	},
	recommendationsTitle: {
		color: theme.other.getColor('text/secondary/default'),
		fontSize: theme.fontSizes.sm,
	},
	suggestionCard: {
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		borderRadius: theme.radius.md,
		padding: theme.spacing.md,
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		textAlign: 'start',
		'&:hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
			borderColor: theme.other.getColor('border/primary/hover'),
		},
	},
	suggestionText: {
		color: theme.other.getColor('text/primary/default'),
		fontSize: theme.fontSizes.sm,
		height: '100%',
		minHeight: space[7],
	},
	skeletonCard: {
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		borderRadius: theme.radius.md,
		padding: theme.spacing.md,
	},
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, 1fr)',
		gap: theme.spacing.sm,
		columnGap: theme.spacing.sm,
		width: '100%',
	},
}));

export default function OnboardingAskAiPage({
	user,
	workspace,
	forceLoading = false,
}: OnboardingAskAiPageProps) {
	const [aiText, setAiText] = useState('');
	const { classes } = useStyles();
	const { fetcherOptions } = useApiContext();

	const variables = {
		body: {
			context: 'onboarding' as const,
			num_prompts: 4,
		},
	};

	const { data: promptCompletions, isLoading: isLoadingSuggestions } = useQuery(
		{
			queryKey: ['suggestPrompts', 'onboarding', 4],
			queryFn: ({ signal }) =>
				fetchSuggestPrompts({ ...fetcherOptions, ...variables }, signal),
			staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
			refetchOnMount: false, // Don't refetch when component mounts if data exists
			refetchOnWindowFocus: false, // Don't refetch when window regains focus
		}
	);

	// Use forceLoading prop to override the loading state
	const isShowingSkeleton = forceLoading || isLoadingSuggestions;

	const handleSuggestionClick = useCallback(
		(suggestion: string) => {
			setAiText(suggestion);
			trackEvent(
				'viewer_onboarding/prompt_suggestion_click',
				{ suggestion },
				user,
				workspace
			);
		},
		[user, workspace]
	);

	const handleSuggestionCardClick = useCallback(
		(suggestion: string) => {
			handleSuggestionClick(suggestion);
		},
		[handleSuggestionClick]
	);

	const handleTextChange = useCallback((text: string) => {
		setAiText(text);
	}, []);

	const humanReadableRoleName = user.role_name
		? user.role_name
				.toLowerCase()
				.split('_')
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ')
		: 'General User';

	return (
		<Stack className={classes.content} spacing="2xl">
			<Stack spacing="sm">
				<Text size="xl" weight="bold" className={classes.title}>
					Ask your data a question
				</Text>
				<Text size="md" className={classes.subtitle}>
					Get the answers you need without the wait. Ask a question and see your
					data work for you.
				</Text>
			</Stack>

			<OnboardingSearchAi
				user={user}
				workspace={workspace}
				value={aiText}
				onTextChange={handleTextChange}
			/>

			<Stack spacing="sm" className={classes.recommendationsSection}>
				{user.role_name && promptCompletions?.suggestions && (
					<Text className={classes.recommendationsTitle}>
						Recommended for your role: {humanReadableRoleName}
					</Text>
				)}
				<Box className={classes.grid}>
					{isShowingSkeleton
						? [1, 2, 3, 4].map((index) => (
								<Box
									key={index}
									className={classes.skeletonCard}
									sx={{
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'center',
									}}
								>
									<Skeleton
										height={32}
										className={classes.suggestionText}
										mb="xs"
									/>
									<Skeleton
										height={32}
										className={classes.suggestionText}
										width="80%"
									/>
								</Box>
							))
						: promptCompletions?.suggestions &&
							promptCompletions.suggestions.length > 0 &&
							promptCompletions.suggestions.map(
								(suggestion: string, index: number) => (
									<UnstyledButton
										key={`suggestion-${index}`}
										className={classes.suggestionCard}
										onClick={() => handleSuggestionCardClick(suggestion)}
										data-testid={`onboarding-suggestion-${index}`}
									>
										<Text className={classes.suggestionText}>{suggestion}</Text>
									</UnstyledButton>
								)
							)}
				</Box>
			</Stack>
		</Stack>
	);
}
